import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import {
  BlockGrid,
  Container,
  ListItem,
  Section,
  SectionTitle,
} from 'chaoskit/src/components'
import { link } from 'chaoskit/src/assets/styles/utility'

import {
  HomeHero,
  HomeHeroGrid,
  HomeHeroGridImage,
  HomeHeroGridContent,
} from '~partials/home/Hero'
import ButtonLink from '~components/ButtonLink'
import Data from '~components/Data'
import Image from '~components/Image'
import Quote from '~components/Quote'
import TextLink from '~components/TextLink'
import CostOfDownTime from '~components/solutions/CostOfDownTime'
import { HeroTitle, HeroContent } from '~partials/Hero'
import BaseLayout from '~layouts/BaseLayout'
import HelmetSEO from '~partials/global/HelmetSEO'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'
import finserveSVG from '~media/solutions/finserve/hero.svg'
import retailSVG from '~media/solutions/retail/hero.svg'
import saasSVG from '~media/solutions/saas/hero.svg'
import arrowSVG from '~media/solutions/finserve/arrow.svg'
import startArrowSVG from '~media/solutions/finserve/start-arrow.svg'
import midArrowSVG from '~media/solutions/finserve/mid-arrow.svg'
import endArrowSVG from '~media/solutions/finserve/end-arrow.svg'
import { ReactComponent as MapImage } from '~media/solutions/finserve/map.svg'
import { ReactComponent as buildResilienceSVG } from '~media/solutions/finserve/build-resilience.svg'
import buildResilienceBGIMG from '~media/solutions/finserve/build-resilience-bg.jpg'
import { ReactComponent as improveAvailabilitySVG } from '~media/solutions/finserve/improve-availability.svg'
import { ReactComponent as manageComplianceSVG } from '~media/solutions/finserve/manage-compliance.svg'
import manageComplianceBGIMG from '~media/solutions/finserve/manage-compliance-bg.jpg'
import { ReactComponent as shiftReliabilitySVG } from '~media/solutions/finserve/shift-reliability.svg'
import { ReactComponent as experimentingSVG } from '~media/solutions/finserve/experimenting.svg'
import { ReactComponent as standardizingSVG } from '~media/solutions/finserve/standardizing.svg'
import { ReactComponent as scalingSVG } from '~media/solutions/finserve/scaling.svg'
import { ReactComponent as EndQuoteSvg } from '~media/global/end_quote.svg'

const Benefit = ({ benefit, length, index }) => {
  const theme = useTheme()

  let BenefitImage
  let BgImage
  const customCSS = {
    display: 'inline-block',
    marginTop: '-10%',
  }

  const containerSpace = {
    paddingBottom: `${theme.space.large}px !important`,
    paddingTop: `${theme.space.large}px !important`,
  }

  switch (benefit.icon) {
    case 'build-resilience':
      BenefitImage = buildResilienceSVG
      BgImage = `url(${buildResilienceBGIMG}) no-repeat right bottom`
      customCSS.maxWidth = '479px'
      customCSS.maxHeight = '326px'
      break
    case 'improve-availability':
      BenefitImage = improveAvailabilitySVG
      BgImage = ''
      customCSS.maxWidth = '527px'
      customCSS.maxHeight = '413px'
      containerSpace.paddingTop = `${theme.space['2xlarge']}px !important`
      break
    case 'manage-compliance':
      BenefitImage = manageComplianceSVG
      BgImage = `url(${manageComplianceBGIMG}) no-repeat left center`
      customCSS.marginTop = '0'
      customCSS.maxWidth = '590px'
      customCSS.maxHeight = '310px'
      break
    case 'shift-reliability':
      BenefitImage = shiftReliabilitySVG
      BgImage = ''
      customCSS.maxWidth = '424px'
      customCSS.maxHeight = '441px'
      containerSpace.paddingBottom = `${theme.space.base}px !important`
      containerSpace.paddingTop = `${theme.space['2xlarge']}px !important`
      break
    default:
      BenefitImage = () => null
      BgImage = ''
      break
  }

  return (
    <Section
      space="large"
      css={{
        background: BgImage,
        backgroundSize: '100% 100%',

        [theme.mq.medium]: {
          ...containerSpace,
        },
      }}
    >
      <Container>
        <div
          css={{
            textAlign: 'center',
            display: 'grid',
            gap: theme.space.large,
            marginBottom: index === length - 1 ? null : theme.space['2xlarge'],

            [theme.mq.medium]: {
              textAlign: 'left',
              gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
              gap: theme.space['2xlarge'],
              marginBottom:
                index === length - 1 ? null : theme.space['3xlarge'],
            },
          }}
        >
          <div
            css={{
              [theme.mq.medium]: {
                gridColumn: index % 2 === 0 ? '6 / span 7' : '1 / span 7',
                gridRow: 1,
                direction: index % 2 === 0 ? null : 'rtl',
                order: index % 2 === 0 ? 1 : null,
                textAlign: index % 2 === 0 ? 'right' : 'left',
              },
            }}
          >
            <BenefitImage
              css={{
                [theme.mq.small__max]: {
                  maxWidth: 400,
                },

                [theme.mq.medium]: customCSS,
              }}
            />
          </div>
          <div
            css={{
              [theme.mq.medium]: {
                gridColumn: index % 2 === 0 ? '1 / span 5' : '8 / span 5',
                gridRow: 1,
              },
            }}
          >
            <h3>
              <Data>{benefit.heading}</Data>
            </h3>
            <div>
              <Data>{benefit.blurb}</Data>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  )
}

Benefit.propTypes = {
  benefit: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    blurb: PropTypes.string.isRequired,
  }),
  length: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
}

const Highlight = ({ highlight, index, length }) => {
  const theme = useTheme()

  let HighlightImage

  switch (highlight.icon) {
    case 'experimenting':
      HighlightImage = experimentingSVG
      break
    case 'standardizing':
      HighlightImage = standardizingSVG
      break
    case 'scaling':
      HighlightImage = scalingSVG
      break
    default:
      HighlightImage = () => null
      break
  }

  let arrowImage
  let arrowPosition

  if (index === 0) {
    arrowImage = startArrowSVG
    arrowPosition = 'right center'
  } else if (index === length - 1) {
    arrowImage = endArrowSVG
    arrowPosition = 'left center'
  } else {
    arrowImage = midArrowSVG
    arrowPosition = 'center center'
  }

  return (
    <ListItem>
      <div
        css={{
          display: 'grid',
          gap: theme.space.small,
          justifyContent: 'center',
          textAlign: 'center',
          alignSelf: 'start',
        }}
      >
        <div
          css={{
            backgroundImage: `url(${arrowSVG})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            padding: theme.space.small,
            marginBottom: theme.space.large,
            ...theme.text.large,
            fontWeight: theme.fontWeight.bold,
            color: theme.color.dark.base,

            [theme.mq.xsmall__max]: {
              backgroundImage: `url(${arrowImage})`,
              backgroundPosition: arrowPosition,
              marginLeft: '-16px',
              marginRight: '-16px',
            },
          }}
        >
          {highlight.topHeading}
        </div>
        <HighlightImage
          css={{ height: 60, display: 'block', margin: 'auto' }}
        />
        <div
          css={{
            ...theme.text.xlarge,
            fontWeight: theme.fontWeight.bold,
            marginBottom: theme.space.medium,
            marginTop: theme.space.small,
          }}
        >
          {highlight.heading}
        </div>
        <div css={{ ...theme.text.small }}>
          <Data>{highlight.blurb}</Data>
        </div>
      </div>
    </ListItem>
  )
}

Highlight.propTypes = {
  highlight: PropTypes.shape({
    topHeading: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    blurb: PropTypes.string.isRequired,
  }),
  index: PropTypes.number.isRequired,
  length: PropTypes.number.isRequired,
}

const FeaturedPost = ({ data }) => {
  const theme = useTheme()

  return (
    <div
      css={[
        link.reset(theme),
        {
          borderTopLeftRadius: theme.borderRadius.base,
          borderTopRightRadius: theme.borderRadius.base,
          position: 'relative',
          zIndex: 1,

          '&::after': {
            content: "''",
            zIndex: -1,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 300,
            background: `linear-gradient(${theme.color.light.base}, ${theme.brand.darkViolet}, ${theme.color.light.base})`,
            opacity: theme.opacity.base / 2,
            filter: 'blur(4px)',
            willChange: 'filter',
            transition: `filter ${theme.timing.base} ${theme.transition.base}`,
          },

          '&:hover, &:focus': {
            '&::after': {
              filter: 'blur(8px)',
              opacity: theme.opacity.base,
            },
          },
        },
      ]}
    >
      <div
        css={{
          height: 0,
          paddingBottom: '50%',
          background: theme.color.dark.base,
          position: 'relative',
          borderTopLeftRadius: theme.borderRadius.base,
          borderTopRightRadius: theme.borderRadius.base,
          overflow: 'hidden',
        }}
      >
        <TextLink href={data.url}>
          <Image
            src={data.featuredImage}
            css={{
              objectFit: 'cover',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectPosition: 'right bottom',
            }}
          />
        </TextLink>
      </div>
      <div
        css={{ padding: theme.space.base, background: theme.color.light.base }}
      >
        <div css={{ marginBottom: theme.space.small }}>
          <TextLink href={data.url}>
            <div
              css={{
                ...theme.text.large,
                color: theme.brand.darkViolet,
                fontWeight: theme.fontWeight.bold,
                margin: 0,
              }}
            >
              {data.title}
            </div>
          </TextLink>
        </div>
        <div
          css={{
            ...theme.text.xsmall,
            color: theme.fontColor.muted,
            marginBottom: theme.space.base,

            span: {
              color: theme.color.dark.base,
            },
          }}
        >
          by <span>{data.author}</span> on <span>{data.date}</span>
        </div>
        <div
          css={{
            ...theme.text.small,
            marginBottom: theme.space.large,
          }}
        >
          {data.excerpt}
        </div>
        <div>
          <ButtonLink
            type="outlinePrimary"
            url={data.url}
            css={{
              [theme.mq.small__max]: {
                marginBottom: `${theme.space.large}px !important`,
                width: '100%',
              },
            }}
          >
            Continue Reading
          </ButtonLink>
          <div
            css={{
              [theme.mq.small__max]: {
                marginTop: theme.space.base,
              },
            }}
          />
        </div>
      </div>
    </div>
  )
}

FeaturedPost.propTypes = {
  data: PropTypes.object.isRequired,
}

const FeaturedPosts = ({ data }) => {
  const theme = useTheme()

  if (data?.edges?.length < 1) return null

  return (
    <div css={{ marginTop: theme.space.large }}>
      <h2>Featured Content</h2>
      <div
        css={{
          display: 'grid',
          gap: theme.space.base,

          [theme.mq.medium]: {
            gridTemplateColumns: 'repeat(3, 1fr)',
          },
        }}
      >
        {data.edges.map(({ node }) => (
          <FeaturedPost
            key={node.fields.slug}
            data={{
              title: node.frontmatter.title,
              author: node.frontmatter.author,
              date: node.frontmatter.date,
              excerpt: node.excerpt,
              featuredImage: node.fields.featuredImage,
              url: node.fields.fullUrl,
            }}
          />
        ))}
      </div>
    </div>
  )
}

FeaturedPosts.propTypes = {
  data: PropTypes.object.isRequired,
}

const SolutionLayout = ({
  type,
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
    featured,
  },
}) => {
  const theme = useTheme()

  let heroImage

  switch (type) {
    case 'finserve':
      heroImage = finserveSVG
      break
    case 'retail':
      heroImage = retailSVG
      break
    case 'saas':
      heroImage = saasSVG
      break
    default:
      heroImage = () => null
      break
  }

  return (
    <BaseLayout
      footerProps={{
        hideCTA: true,
      }}
    >
      <HelmetSEO
        title={pageData.title}
        description={pageData.description}
        image={pageData.socialMediaImage}
      />
      <NodeTypeTracker nodeType="solutions" />
      <HomeHero
        css={{
          background:
            'radial-gradient(46.77% 147.55% at 69.9% 62.72%, #311173 0%, #1F1638 100%)',
        }}
      >
        <HeroContent>
          <HomeHeroGrid>
            <HomeHeroGridImage>
              <Image
                src={heroImage}
                alt="hero-image"
                css={{
                  [theme.mq.medium]: {
                    height: 'auto !important',
                    maxHeight: '580px',
                    maxWidth: '580px !important',
                  },
                }}
              />
            </HomeHeroGridImage>
            <HomeHeroGridContent>
              <HeroTitle as="h5" css={{ textTransform: 'uppercase' }}>
                <Data>{pageData.hero.subHeading}</Data>
              </HeroTitle>
              <HeroTitle as="h2">
                <Data>{pageData.hero.heading}</Data>
              </HeroTitle>
              <div
                css={{
                  ...theme.text.large,
                }}
              >
                <Data>{pageData.hero.blurb}</Data>
              </div>
              <div css={{ marginTop: theme.space.large }}>
                <ButtonLink
                  noContrast
                  type="primary"
                  url={pageData.hero.buttonURL}
                  css={{
                    [theme.mq.small__max]: {
                      width: '100%',
                    },
                  }}
                >
                  {pageData.hero.buttonText}
                </ButtonLink>
              </div>
            </HomeHeroGridContent>
          </HomeHeroGrid>
        </HeroContent>
      </HomeHero>
      <Section>
        <Container>
          <div
            css={{
              [theme.mq.small__max]: {
                margin: theme.space.base,
              },
            }}
          >
            <Quote
              contrastQuotes
              css={{
                paddingBottom: theme.space.large,
                blockquote: {
                  div: {
                    fontSize: `60px !important`,
                  },
                },

                '.G__Quote__Author': {
                  ...theme.text.large,
                  fontWeight: theme.fontWeight.bold,

                  '&::before': {
                    content: "'- '",
                  },
                },
              }}
              author={<Data>{pageData.quoteSection.quoteFrom}</Data>}
            >
              <Data>{pageData.quoteSection.quote}</Data>
              <EndQuoteSvg
                css={{
                  bottom: 0,
                  right: 0,
                  zIndex: -1,
                  position: 'absolute',
                  pointerEvents: 'none',
                  width: theme.fontSize.xlarge,
                  height: theme.fontSize.xlarge,
                  transform: 'translate(-65%, -40%)',

                  [theme.mq.medium]: {
                    bottom: '-42px',
                    right: '-65px',
                    width: theme.fontSize.large * 2,
                    height: theme.fontSize.large * 2,
                    transform: 'translate(-65%, -50%)',
                  },
                }}
              />
            </Quote>
          </div>
          <div
            css={{
              ...theme.text.large,
              padding: theme.space.large,
              background: theme.brand.stone50,
            }}
          >
            <Data>{pageData.quoteSection.blurb}</Data>
          </div>
        </Container>
      </Section>
      <Section
        className="u-contrast"
        css={{
          background:
            'radial-gradient(46.77% 147.55% at 69.9% 62.72%, #311173 0%, #1F1638 100%)',
          paddingBottom: `0px !important`,

          [theme.mq.small__max]: {
            paddingBottom: `${theme.space.base}px !important`,
          },
        }}
      >
        <Container>
          <SectionTitle
            as="h2"
            css={{
              marginBottom: '0 !important',
            }}
            title={pageData.mapSection.heading}
            sub={pageData.mapSection.subHeading}
          />
          <MapImage css={{ width: '100%' }} />
        </Container>
      </Section>
      <Section
        css={{
          paddingBottom: '0 !important',
        }}
      >
        <Container>
          <SectionTitle as="h2" title={pageData.benefitsSection.heading} />
          <div />
        </Container>
        {pageData.benefitsSection.items.map((benefit, index) => (
          <Benefit
            key={benefit.heading}
            benefit={benefit}
            length={pageData.benefitsSection.items.length}
            index={index}
          />
        ))}
      </Section>
      {pageData.showCostOfDowntimeSection && (
        <CostOfDownTime
          heading={pageData.costOfDowntimeSection.heading}
          content={pageData.costOfDowntimeSection.blurb}
        />
      )}
      <Section
        className="u-contrast"
        css={{
          background:
            'radial-gradient(46.77% 147.55% at 69.9% 62.72%, #311173 0%, #1F1638 100%)',
        }}
      >
        <Container>
          <SectionTitle
            as="h2"
            title={pageData.demoSection.heading}
            sub={pageData.demoSection.subHeading}
          />
          <BlockGrid
            size={{ medium: 2, large: 3 }}
            gutter={{ base: 'large', large: 'xlarge' }}
            css={{ justifyContent: 'center' }}
          >
            {pageData.demoSection.items.map((highlight, index) => (
              <Highlight
                key={highlight.heading}
                highlight={highlight}
                index={index}
                length={pageData.demoSection.items.length}
              />
            ))}
          </BlockGrid>
          <div
            css={{
              marginTop: theme.space.xlarge,
              paddingBottom: theme.space.large,
              display: 'grid',
              justifyItems: 'center',
              textAlign: 'center',
            }}
          >
            <ButtonLink
              noContrast
              type="primary"
              url={pageData.demoSection.demoURL}
              css={{
                [theme.mq.small__max]: {
                  width: '100%',
                },
              }}
            >
              {pageData.demoSection.demoText}
            </ButtonLink>
          </div>
        </Container>
      </Section>
      {pageData.showFeaturedContent && (
        <Section>
          <Container>
            <FeaturedPosts data={featured} />
          </Container>
        </Section>
      )}
    </BaseLayout>
  )
}

SolutionLayout.propTypes = {
  type: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
}

export default SolutionLayout
